/**
 * 💡 ICEBREAKERS PANEL - Suggestions d'accroche par Pixie
 * 
 * Génère des phrases d'accroche personnalisées basées sur :
 * - Le profil de la personne
 * - Ses passions
 * - Sa bio
 * - Le contexte
 * 
 * Pixie la commandante utilise ses talents pour créer des messages parfaits !
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wand2, Copy, RefreshCw, Sparkles, MessageCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PIXIE } from '../../core/HeartbeatsProvider';
import { Button } from '../../../ui/button';
import { PixieAvatar, PixieInline } from '../../PixieAvatar';

// 🎯 Templates d'icebreakers par catégorie
const ICEBREAKER_TEMPLATES = {
  passion: [
    "J'ai vu que t'aimais {passion}... C'est quoi ton meilleur souvenir lié à ça ? 😊",
    "{passion} ? Pareil ! On devrait en parler autour d'un café ☕",
    "Dis-moi que t'as pas mis {passion} juste pour faire genre... Parce que moi c'est ma vie ! 🔥",
    "Si tu devais me convaincre de {passion} en 3 mots, tu dirais quoi ? 🤔",
    "{passion} = instant connexion ! Tu pratiques depuis longtemps ? 💜"
  ],
  funny: [
    "Je suis nul(le) en premiers messages, alors je laisse Pixie ma commandante parler... 🚀✨",
    "Sur une échelle de 1 à 10, à quel point tu regrettes d'avoir swipé à droite ? 😂",
    "C'est quoi ton excuse préférée pour annuler un date ? (C'est pour un ami) 🙈",
    "Plot twist : et si on sautait les 47 messages de small talk et qu'on se racontait direct un truc gênant ? 🎭",
    "J'ai demandé à ChatGPT une phrase d'accroche mais il m'a dit de juste être moi-même... Bref, salut ! 🤖😅"
  ],
  deep: [
    "C'est quoi le dernier truc qui t'a fait sourire comme un(e) idiot(e) ? 😊",
    "Si tu pouvais revivre une journée parfaite de ta vie, ce serait laquelle ? 🌟",
    "Qu'est-ce que tu ferais si t'avais pas peur du jugement des autres ? 🦋",
    "C'est quoi le conseil que tu donnerais à ton toi d'il y a 5 ans ? 💭",
    "Si ta vie était un film, ce serait quel genre ? (Moi je dirais comédie romantique chaotique) 🎬"
  ],
  flirty: [
    "J'essaie de trouver une phrase d'accroche originale mais ton sourire m'a fait bugger... 🫠",
    "Est-ce que c'est moi ou y'a vraiment une connexion là ? 💫",
    "Je crois que Pixie a fait de la magie parce que je peux pas m'empêcher de revenir sur ton profil... 🚀",
    "Avoue, tu fais exprès d'être aussi {adjective} ou c'est naturel ? 😏",
    "Je sais pas toi, mais moi j'ai l'impression qu'on va bien s'entendre... 💜"
  ],
  creative: [
    "Imagine : premier date, on fait quoi ? (Mauvaises réponses uniquement) 🎪",
    "Tu préfères : voyage dans le temps OU lire dans les pensées ? Et pourquoi ? 🤔",
    "Si t'étais un emoji, tu serais lequel et pourquoi ? (Moi c'est 🌀 parce que ma vie c'est le chaos) 😂",
    "On joue à 'Tu préfères' ? Je commence : Pizza froide OU céréales chaudes ? 🍕🥣",
    "Raconte-moi un truc random sur toi que personne te demande jamais ! 🎲"
  ]
};

// 🎨 Adjectifs pour les templates
const ADJECTIVES = ['canon', 'craquant(e)', 'stylé(e)', 'intrigant(e)', 'magnifique'];

export function IcebreakersPanel({ 
  targetProfile, 
  onSelectIcebreaker, 
  isDarkMode = false,
  compact = false 
}) {
  const [category, setCategory] = useState('passion');
  const [icebreakers, setIcebreakers] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // 🪄 Générer des icebreakers personnalisés
  const generateIcebreakers = useCallback(() => {
    setIsGenerating(true);
    
    // Extraire les passions du profil cible
    const passions = targetProfile?.passions 
      ? Object.values(targetProfile.passions).flat() 
      : ['la vie', 'les voyages', 'la musique'];
    
    const templates = ICEBREAKER_TEMPLATES[category] || ICEBREAKER_TEMPLATES.passion;
    
    // Générer 3 icebreakers personnalisés
    const generated = templates
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map(template => {
        return template
          .replace('{passion}', passions[Math.floor(Math.random() * passions.length)])
          .replace('{adjective}', ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]);
      });
    
    setTimeout(() => {
      setIcebreakers(generated);
      setIsGenerating(false);
    }, 500); // Petit délai pour l'effet magique
  }, [category, targetProfile]);
  
  // 📋 Copier un icebreaker
  const copyIcebreaker = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('🚀 Copié ! Pixie te souhaite bonne chance ! ✨');
    setTimeout(() => setCopiedIndex(null), 2000);
    
    if (onSelectIcebreaker) {
      onSelectIcebreaker(text);
    }
  };
  
  // Générer au premier rendu
  React.useEffect(() => {
    generateIcebreakers();
  }, [category]);
  
  const categories = [
    { id: 'passion', label: '🎯 Passions', color: 'pink' },
    { id: 'funny', label: '😂 Drôle', color: 'yellow' },
    { id: 'deep', label: '💭 Profond', color: 'blue' },
    { id: 'flirty', label: '😏 Flirty', color: 'red' },
    { id: 'creative', label: '🎨 Créatif', color: 'purple' }
  ];
  
  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <PixieAvatar size="sm" />
          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Pixie te suggère...
          </span>
          <button
            onClick={generateIcebreakers}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="space-y-1">
          {icebreakers.slice(0, 2).map((text, idx) => (
            <button
              key={idx}
              onClick={() => copyIcebreaker(text, idx)}
              className={`w-full text-left p-2 rounded-lg text-xs transition-all ${
                isDarkMode 
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                  : 'bg-gray-100 hover:bg-pink-50 text-gray-700'
              }`}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <PixieAvatar size="md" withGlow />
          </motion.div>
          <div>
            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Icebreakers magiques
            </h3>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Pixie te suggère des accroches parfaites ✨
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={generateIcebreakers}
          disabled={isGenerating}
        >
          <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {/* Catégories */}
      <div className="flex flex-wrap gap-2 mb-4">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              category === cat.id
                ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>
      
      {/* Icebreakers */}
      <div className="space-y-2">
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center py-8"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Wand2 className="w-8 h-8 text-pink-500" />
              </motion.div>
              <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Pixie prépare sa magie...
              </span>
            </motion.div>
          ) : (
            <motion.div
              key="icebreakers"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              {icebreakers.map((text, idx) => (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => copyIcebreaker(text, idx)}
                  className={`w-full text-left p-3 rounded-xl transition-all group ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600' 
                      : 'bg-gray-50 hover:bg-pink-50 border border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {text}
                    </p>
                    <div className={`p-1.5 rounded-full transition-colors ${
                      copiedIndex === idx 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-500 group-hover:bg-pink-500 group-hover:text-white'
                    }`}>
                      {copiedIndex === idx ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </div>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Footer tip */}
      <div className={`mt-4 p-2 rounded-lg text-center ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
        <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          💫 Clique sur une suggestion pour la copier !
        </p>
      </div>
    </div>
  );
}

export default IcebreakersPanel;
